import React, { useState, useEffect, useRef } from 'react';
import { Language } from '@/types';
import { Modal, Button, Input, Loading } from '@/components/common';
import { getTranslation } from '@/constants';
import { useChat } from '@/hooks';
import { useAuth } from '@/context/AuthContext';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  exchangeId: string | null;
  language: Language;
}

export const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose, exchangeId, language }) => {
  const { user, userProfile } = useAuth();
  const { messages, sendMessage: sendMsg, loading, markAsRead } = useChat(
    exchangeId,
    user?.uid,
    userProfile?.displayName || userProfile?.email
  );
  const t = getTranslation(language);

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && exchangeId) {
      markAsRead();
    }
  }, [isOpen, exchangeId, markAsRead]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await sendMsg(newMessage);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t.chatTitle}
      size="lg"
    >
      <div className="flex flex-col h-[500px]">
        {/* Messages List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 rounded">
          {loading && messages.length === 0 ? (
            <Loading size="sm" />
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">{t.noMessages}</p>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                const isOwnMessage = message.senderId === user?.uid;

                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg px-4 py-2 ${
                        isOwnMessage
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-900 border'
                      }`}
                    >
                      {!isOwnMessage && (
                        <p className="text-xs font-medium mb-1 opacity-75">
                          {message.senderName}
                        </p>
                      )}
                      <p className="text-sm">{message.message}</p>
                      <p
                        className={`text-xs mt-1 ${
                          isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                        }`}
                      >
                        {new Date(message.createdAt).toLocaleTimeString(
                          language === 'en' ? 'en-US' : 'fr-FR',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        <form onSubmit={handleSendMessage} className="mt-4 flex space-x-2">
          <Input
            type="text"
            placeholder={t.typeMessage}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={sending}
            className="flex-1"
          />
          <Button type="submit" disabled={!newMessage.trim()} loading={sending}>
            {t.sendMessage}
          </Button>
        </form>
      </div>
    </Modal>
  );
};
